// MainApp.js
import Header from './components/layout/Header';
import ChatView from './views/ChatView';
import UploadView from './views/UploadView';
import HistoryView from './views/HistoryView';
import Footer from './components/layout/Footer';
import Sidebar from './components/layout/Sidebar';
import { useState, useEffect } from 'react';
import SettingsPage from './views/SettingsPage';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { Routes, Route, Navigate } from 'react-router-dom';
import SpacesView from './views/SpacesView';
import SpaceDetail from './views/SpaceDetail';
import ActionLoader from './components/upload/ActionLoader';
import DocumentActionsPanel from './components/upload/DocumentActionsPanel';
import ResearchView from './views/ResearchView';

export default function MainApp({ isDarkMode, toggleTheme }) {
  const [activeTab, setActiveTab] = useState('chat');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [allMessages, setAllMessages] = useState({
    current: [],
  });
  const [chatHistory, setChatHistory] = useState([]);
  const [resources, setResources] = useState(null);
  const [currentChatId, setCurrentChatId] = useState('current');
  const TOUR_DELAY = 2000;
  useEffect(() => {
    const hasSeenTour = localStorage.getItem('hasSeenTour');
    const upgradeModalOpen = sessionStorage.getItem('upgrade_modal_open');
    const upgradeIntent = sessionStorage.getItem('upgrade_intent');

    // 🛑 Absolute priority: upgrade flow
    if (upgradeIntent) return;

    if (!hasSeenTour && !upgradeModalOpen) {
      const timer = setTimeout(() => {
        startTour();
        localStorage.setItem('hasSeenTour', 'true');
      }, TOUR_DELAY);

      return () => clearTimeout(timer);
    }
  }, []);

  const startTourSafely = () => {
    if (!localStorage.getItem('hasSeenTour')) {
      startTour();
      localStorage.setItem('hasSeenTour', 'true');
    }
  };

  const createNewChat = () => {
    setCurrentChatId('current');
    navigate('/app/chat');

    setAllMessages((prev) => ({
      ...prev,
      current: [],
    }));
  };

  const fetchDocumentDetails = async (documentId, sourceId) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          id: documentId || 'doc123',
          title: 'Legal Agreement',
          content: 'Sample content here...',
          highlightedParts: [],
        });
      }, 500);
    });
  };
  const startTour = () => {
    const interval = setInterval(() => {
      const chatEl = document.querySelector('.chat-tour');
      const researchEl = document.querySelector('.research-tour');
      const uploadEl = document.querySelector('.upload-tour');

      if (chatEl && researchEl && (uploadEl || isMobile)) {
        clearInterval(interval);

        const steps = [
          {
            element: '.chat-tour',
            popover: {
              title: 'AI ChatBot',
              description: 'Ask your Legal Questions here.',
              side: 'bottom',
              align: 'center',
            },
            onNextClick: () =>
              document
                .querySelector('.research-tour')
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
          },
          {
            element: '.research-tour',
            popover: {
              title: 'Research',
              description:
                'Make in-depth Professional Research with Sources and Reports.',
            },
            onNextClick: () => {
              if (!isMobile) {
                document
                  .querySelector('.upload-tour')
                  ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            },
          },
        ];

        if (!isMobile) {
          steps.push({
            element: '.upload-tour',
            popover: {
              title: 'Document Check',
              description:
                'Upload your Documents and Analyze them using our Predetermined Checks or ask Legal Questions.',
            },
          });
        }

        const tour = driver({
          showProgress: true,
          allowClose: true,
          steps,
        });

        tour.drive();
      }
    }, 300);
  };

  return (
    <>
      <div
        className={`flex max-h-screen overflow-x-hidden overflow-y-visible ${
          isDarkMode ? 'bg-gray-800 text-white' : 'bg-gray-50 text-gray-900'
        }`}
      >
        <Sidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          createNewChat={createNewChat}
          chatHistory={chatHistory}
          isDarkMode={isDarkMode}
          isSidebarCollapsed={isSidebarCollapsed}
          setIsSidebarCollapsed={setIsSidebarCollapsed}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          isMobile={isMobile}
          allMessages={allMessages}
          onUpgradeFlowComplete={startTourSafely}
        />
        {isMobile && isSidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black bg-opacity-50"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        <div className="flex-1 flex flex-col overflow-hidden min-h-screen">
          <Header
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            isDarkMode={isDarkMode}
            toggleTheme={toggleTheme}
            currentChatId={currentChatId}
            chatHistory={chatHistory}
            setChatHistory={setChatHistory}
            resources={resources}
            startTour={startTour}
            isMobile={isMobile}
            toggleSidebar={() => {
              setIsSidebarOpen((prev) => {
                const willOpen = !prev;
                if (willOpen) setIsSidebarCollapsed(false);
                return willOpen;
              });
            }}
          />
          <main
            className={`flex-grow flex min-w-0 overflow-y-auto overflow-x-hidden ${
              isDarkMode ? 'bg-gray-900' : 'bg-white'
            }`}
          >
            <Routes>
              <Route
                path="researches"
                element={<ResearchView isDarkMode={isDarkMode} />}
              />
              <Route
                path="chat"
                element={
                  <ChatView
                    allMessages={allMessages}
                    setAllMessages={setAllMessages}
                    isDarkMode={isDarkMode}
                    fetchDocumentDetails={fetchDocumentDetails}
                    setChatHistory={setChatHistory}
                    setResources={setResources}
                    resources={resources}
                  />
                }
              />
              <Route
                path="chat/:chatId"
                element={
                  <ChatView
                    allMessages={allMessages}
                    setAllMessages={setAllMessages}
                    isDarkMode={isDarkMode}
                    fetchDocumentDetails={fetchDocumentDetails}
                    setChatHistory={setChatHistory}
                    setResources={setResources}
                    resources={resources}
                  />
                }
              />
              <Route
                path="research/:chatId"
                element={
                  <ChatView
                    allMessages={allMessages}
                    setAllMessages={setAllMessages}
                    isDarkMode={isDarkMode}
                    fetchDocumentDetails={fetchDocumentDetails}
                    setChatHistory={setChatHistory}
                    resources={resources}
                  />
                }
              />
              <Route
                path="spaces"
                element={<SpacesView isDarkMode={isDarkMode} />}
              />
              <Route
                path="spaces/:spaceId"
                element={<SpaceDetail isDarkMode={isDarkMode} />}
              />
              <Route
                path="document"
                element={<UploadView isDarkMode={isDarkMode} />}
              />
              <Route
                path="document/action"
                element={<DocumentActionsPanel isDarkMode={isDarkMode} />}
              />
              <Route
                path="document/action/:type/:sessionId?"
                element={<ActionLoader isDarkMode={isDarkMode} />}
              />
              {/* <Route
              path="document/action/:type"
              element={<ActionLoader isDarkMode={isDarkMode} />}
            /> */}
              <Route
                path="history"
                element={
                  <HistoryView
                    chatHistory={chatHistory}
                    createNewChat={createNewChat}
                    isDarkMode={isDarkMode}
                  />
                }
              />
              <Route
                path="settings"
                element={
                  <SettingsPage
                    isDarkMode={isDarkMode}
                    toggleTheme={toggleTheme}
                  />
                }
              />
              <Route
                path="*"
                element={<Navigate to="/app/chat" replace />}
              />{' '}
            </Routes>
          </main>

          <Footer isDarkMode={isDarkMode} />
        </div>
      </div>
    </>
  );
}
